import {
  Divider,
  Flex,
  ReportSection,
  ReportSectionItemContent,
  Text,
} from '@/components';
import type { LedgerSsBasicInfoSectionProps } from '@/features/mold/models/ledger';
import {
  getLegalBaseTableData,
  getMinistryName,
  getNestedOrganizationsName,
  getOtherOrganizationInChargeName,
  getPlanTableData,
  getRelatedProjectNames,
} from '@/features/mold/utils';
import {
  ITEM_SPLITTER,
  formatProjectEndYearForPdf,
  formatProjectStartYearForPdf,
} from '@/utils';
import { ProjectCategoryLabelModifier } from '@/utils/enum';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TableContent } from './TableContent';

export const SsBasicInfoSection: FC<LedgerSsBasicInfoSectionProps> = ({
  project,
  ministries,
  organizations,
  concatenatedBusinessManagerName,
  independentAdministrativeAgency,
}) => {
  const { t } = useTranslation();

  const ministryName = getMinistryName({
    targetId: project?.ministryId,
    ministries,
  });

  return (
    <>
      <Flex vertical gap={8}>
        <Flex gap={16}>
          <Text
            text={t('segment-sheet-report-title', {
              year: project?.fiscalYear,
            })}
            variant="footnote"
          />
          <Text text={ministryName} variant="footnote" />
        </Flex>
        <Text text={project?.name} variant="h4" />
      </Flex>
      <Divider />
      <ReportSection
        title={t('basic-information')}
        items={[
          {
            subtitle: t('organization-information'),
            item: (
              <Flex vertical>
                <ReportSectionItemContent
                  label={t('ministry')}
                  value={ministryName}
                />
                <ReportSectionItemContent
                  label={t('organization-in-charge')}
                  value={getNestedOrganizationsName({
                    project,
                    organizations,
                  })}
                />
                <ReportSectionItemContent
                  label={t('business-manager')}
                  value={concatenatedBusinessManagerName}
                />
                <ReportSectionItemContent
                  label={t('other-organization-in-charge')}
                  value={getOtherOrganizationInChargeName({
                    project,
                    organizations,
                  })}
                  isLast
                />
              </Flex>
            ),
          },
          {
            subtitle: t('basic-information'),
            item: (
              <div>
                <ReportSectionItemContent
                  label={t('independent-administrative-agency')}
                  value={independentAdministrativeAgency?.name}
                />
                <Flex>
                  <ReportSectionItemContent
                    label={t('project-number')}
                    value={project?.projectNumber}
                  />
                  <ReportSectionItemContent
                    label={t('start-fiscal-year-of-project')}
                    value={formatProjectStartYearForPdf(project?.startYear)}
                    hasLeftBorder
                  />
                  <ReportSectionItemContent
                    labelStyle={{ width: 150 }}
                    label={t('end-fiscal-year-of-project')}
                    value={formatProjectEndYearForPdf(project?.endYear)}
                    hasLeftBorder
                  />
                </Flex>
                <Flex>
                  <ReportSectionItemContent
                    label={t('fiscal-year')}
                    value={project?.fiscalYear}
                    isLast
                  />
                  <ReportSectionItemContent
                    label={t('project-category')}
                    value={
                      project?.category &&
                      ProjectCategoryLabelModifier[project.category]
                    }
                    hasLeftBorder
                    isLast
                  />
                </Flex>
              </div>
            ),
          },
          {
            subtitle: t('related-projects'),
            innerStyle: { display: 'flex', flexDirection: 'row' },
            item: (
              <>
                <ReportSectionItemContent
                  value={getRelatedProjectNames({ project })}
                  isLast
                />
                <ReportSectionItemContent
                  label={t('main-cost')}
                  value={project?.mainCosts
                    .map((d) => d.name)
                    .join(ITEM_SPLITTER)}
                  hasLeftBorder
                  isLast
                />
              </>
            ),
          },
          {
            subtitle: t('segment-unit-concept'),
            isLongText: true,
            item: (
              <ReportSectionItemContent
                value={project?.segmentUnitConcept}
                isLast
              />
            ),
          },
          {
            subtitle: t('overview-and-purpose'),
            item: (
              <div>
                <ReportSectionItemContent
                  label={t('project-purpose')}
                  value={project?.purpose}
                />
                <ReportSectionItemContent
                  label={t('project-task')}
                  value={project?.task}
                />
                <ReportSectionItemContent
                  label={t('project-overview')}
                  value={project?.overview}
                />
                <ReportSectionItemContent
                  label={t('overview-url')}
                  value={project?.overviewUrl}
                  isLast
                />
              </div>
            ),
          },
          {
            subtitle: t('legal-bases'),
            item: (
              <TableContent
                noneBorder
                data={getLegalBaseTableData({ project })}
              />
            ),
          },
          {
            subtitle: t('plan'),
            item: (
              <TableContent noneBorder data={getPlanTableData({ project })} />
            ),
          },
          {
            subtitle: t('remark'),
            isLongText: true,
            item: <ReportSectionItemContent value={project?.remark} isLast />,
          },
        ]}
      />
    </>
  );
};
